import { useState } from 'react';

import { avatarColors, initialsOf } from '@/design-system/lib/avatar';
import { cn } from '@/design-system/lib/cn';

/** Diâmetros usados no design (px). */
const SIZES = {
  xs: 'size-6 text-micro',
  sm: 'size-7 text-micro',
  md: 'size-8 text-micro',
  lg: 'size-9 text-caption',
  xl: 'size-9.5 text-meta',
  '2xl': 'size-12 text-body',
  '3xl': 'size-17 text-display',
} as const;

export type AvatarSize = keyof typeof SIZES;

export type AvatarProps = {
  name: string;
  /** Endereço da foto; sem ela (ou se não carregar), as iniciais. */
  photo?: string | null;
  size?: AvatarSize;
  /** Borda branca para empilhar. */
  ring?: boolean;
  /** Decorativo quando o nome já aparece ao lado. */
  decorative?: boolean;
  className?: string;
};

/** A foto, ou as iniciais sobre a cor derivada do nome quando não há foto. */
export function Avatar({
  name,
  photo,
  size = 'lg',
  ring = false,
  decorative = true,
  className,
}: AvatarProps) {
  const colors = avatarColors(name);
  // A foto que falhou fica nas iniciais; outra foto tenta de novo.
  const [failed, setFailed] = useState<string | null>(null);
  const shows = !!photo && failed !== photo;

  return (
    <span
      aria-hidden={decorative || undefined}
      role={decorative ? undefined : 'img'}
      aria-label={decorative ? undefined : name}
      title={decorative ? undefined : name}
      style={{ backgroundColor: colors.bg, color: colors.fg }}
      className={cn(
        'relative grid shrink-0 place-items-center overflow-hidden rounded-pill leading-none font-semibold',
        SIZES[size],
        ring && 'border-2 border-white',
        className
      )}
    >
      {initialsOf(name)}
      {shows ? (
        <img
          src={photo}
          alt=""
          loading="lazy"
          decoding="async"
          draggable={false}
          onError={() => setFailed(photo)}
          className="absolute inset-0 size-full object-cover"
        />
      ) : null}
    </span>
  );
}

export type AvatarPerson = { name: string; photo?: string | null };

export type AvatarStackProps = {
  people: readonly AvatarPerson[];
  max?: number;
  size?: 'xs' | 'sm';
  className?: string;
};

/** Avatares sobrepostos com "+N" para o excedente. */
export function AvatarStack({ people, max = 5, size = 'sm', className }: AvatarStackProps) {
  const shown = people.slice(0, max);
  const rest = people.length - shown.length;

  return (
    <span
      role="img"
      aria-label={people.map((p) => p.name).join(', ')}
      className={cn('flex pl-1.5', className)}
    >
      {shown.map((p, i) => (
        <Avatar key={`${p.name}-${i}`} name={p.name} photo={p.photo} size={size} ring className="-ml-1.5" />
      ))}
      {rest > 0 ? (
        <span
          aria-hidden
          className={cn(
            '-ml-1.5 grid min-w-6 place-items-center rounded-pill border-2 border-white bg-surface-muted px-1.5 text-micro font-semibold text-ink-muted',
            size === 'xs' ? 'h-6' : 'h-7'
          )}
        >
          +{rest}
        </span>
      ) : null}
    </span>
  );
}
